import { PureComponent } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import Cookies from 'js-cookie'
import { getEnvironmentConfig, refreshToken } from 'core/actions/Auth.actions'
import { fetchProfile, fetchUserProfile, fetchUserApplications } from 'core/actions/Profile.actions'
import { IActionCreatorAsync } from 'core/actions/Actions.interfaces'
import { IAppState } from 'core/store/store'
import { ACCESS_TOKEN_LS_KEY, REFRESH_TOKEN_LS_KEY, SESSION_ACTIVE_COOKIE_NAME } from 'core/utils/constants'
import { isOauthFlow } from 'shared/helpers/utils'
import { setItemToLS } from 'shared/helpers/LocalStorage'

interface IProps {
  refreshToken: IActionCreatorAsync
  fetchProfile: IActionCreatorAsync
  fetchUserProfile: IActionCreatorAsync
  fetchUserApplications: IActionCreatorAsync
  getEnvironmentConfig: IActionCreatorAsync
  expiresAt: string
}

class RefreshToken extends PureComponent<IProps> {
  private refreshTimer: number | null = null

  public async componentDidMount (): Promise<void> {
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.props.getEnvironmentConfig().then(async (res) => {
      if (res) {
        setItemToLS(REFRESH_TOKEN_LS_KEY, res.refresh_token_cookie_name)
        setItemToLS(ACCESS_TOKEN_LS_KEY, res.access_token_cookie_name)
        const urlSearchParams = new URLSearchParams(window.location.search);
        const stateVal = urlSearchParams.get('state')
        if (!isOauthFlow(stateVal) && Cookies.get(SESSION_ACTIVE_COOKIE_NAME)) {
          await this.props.refreshToken()
          if (!window.location.pathname.includes('/app/account/agreement')) {
            this.props.fetchProfile()
            this.props.fetchUserProfile(true)
            this.props.fetchUserApplications()
          }
        }
      }
    })
  }

  public componentWillUnmount (): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      const { expiresAt } = this.props
      if (!expiresAt) return
      const msUntilExpiry = moment.utc(expiresAt).diff(moment.utc())
      if (msUntilExpiry < 10 * 1000) {
        // Clear any pending timer to avoid a concurrent refresh firing moments later
        if (this.refreshTimer !== null) {
          clearTimeout(this.refreshTimer)
          this.refreshTimer = null
        }
        this.props.refreshToken()
      }
    }
  }

  public componentDidUpdate (prevProps: Readonly<IProps>): void {
    const { expiresAt } = this.props

    if (expiresAt && expiresAt !== prevProps.expiresAt) {
      if (this.refreshTimer !== null) {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = null
      }
      const REFRESH_BUFFER_MS = 10 * 1000 // refresh 10s before expiry
      const timeout: number = moment.utc(expiresAt).diff(moment.utc()) - REFRESH_BUFFER_MS
      this.refreshTimer = window.setTimeout(() => this.props.refreshToken(), Math.max(timeout, 0))
    }
  }

  public render = (): null => null
}

export default connect(
  ({ auth }: IAppState) => ({ expiresAt: auth.expiresAt }),
  { refreshToken, fetchProfile, fetchUserProfile, fetchUserApplications, getEnvironmentConfig }
)(RefreshToken)
